import { useState } from "react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { 
  Mail, 
  Phone, 
  MessageSquare, 
  ArrowLeft,
  Send,
  Loader2,
  CheckCircle,
  HelpCircle,
  FileQuestion,
  Car
} from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";

export default function PublicSupportPage() {
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    subject: "",
    category: "",
    vin: "",
    message: ""
  });
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.name || !formData.email || !formData.message) {
      toast.error("Please fill in all required fields");
      return;
    }

    setSubmitting(true);
    try {
      await api.post("/support/public-inquiry", {
        name: formData.name,
        email: formData.email,
        subject: formData.subject || `Support Request: ${formData.category || "General"}`,
        category: formData.category,
        vin: formData.vin,
        message: formData.message
      });
      
      setSubmitted(true);
      toast.success("Your message has been sent. We'll get back to you soon!");
    } catch (error) {
      toast.error("Failed to send message. Please try again or email us directly.");
    } finally {
      setSubmitting(false);
    }
  };

  if (submitted) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-slate-50 to-white">
        {/* Header */}
        <header className="bg-white border-b border-slate-200">
          <div className="max-w-4xl mx-auto px-4 py-4">
            <Link to="/" className="flex items-center gap-2">
              <img 
                src="https://images.losspay.com/losspay/losspay-logo.png" 
                alt="LossPay" 
                className="h-8"
              />
            </Link>
          </div>
        </header>

        <div className="max-w-2xl mx-auto px-4 py-16 text-center">
          <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
            <CheckCircle className="h-8 w-8 text-green-600" />
          </div>
          <h1 className="text-2xl font-bold text-slate-900 mb-4" style={{ fontFamily: 'Manrope, sans-serif' }}>
            Message Sent Successfully
          </h1>
          <p className="text-slate-600 mb-8">
            Thank you for contacting us. Our support team will review your message and get back to you within 1-2 business days.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button
              variant="outline"
              onClick={() => {
                setSubmitted(false);
                setFormData({
                  name: "",
                  email: "",
                  subject: "",
                  category: "",
                  vin: "",
                  message: ""
                });
              }}
            >
              Send Another Message
            </Button>
            <Link to="/quote">
              <Button variant="blue">
                Get a Quote
              </Button>
            </Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-50 to-white">
      {/* Header */}
      <header className="bg-white border-b border-slate-200">
        <div className="max-w-4xl mx-auto px-4 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2">
            <img 
              src="https://images.losspay.com/losspay/losspay-logo.png" 
              alt="LossPay" 
              className="h-8"
            />
          </Link>
          <Link 
            to="/quote" 
            className="text-sm text-blue-600 hover:text-blue-800 flex items-center gap-1"
          >
            <ArrowLeft className="h-4 w-4" />
            Back to Quote
          </Link>
        </div>
      </header>

      <div className="max-w-4xl mx-auto px-4 py-8 sm:py-12">
        {/* Page Title */}
        <div className="text-center mb-10">
          <h1 className="text-3xl sm:text-4xl font-bold text-slate-900 mb-3" style={{ fontFamily: 'Manrope, sans-serif' }}>
            How Can We Help?
          </h1>
          <p className="text-slate-600 max-w-2xl mx-auto">
            Our support team is here to assist you with any questions about vehicle eligibility, coverage options, or your quote.
          </p>
        </div>

        <div className="grid md:grid-cols-3 gap-8">
          {/* Contact Info Cards */}
          <div className="md:col-span-1 space-y-4">
            <div className="bg-white rounded-xl border border-slate-200 p-5 shadow-sm">
              <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center mb-3">
                <Mail className="h-5 w-5 text-blue-600" />
              </div>
              <h3 className="font-semibold text-slate-900 mb-1">Email Us</h3>
              <a 
                href="mailto:support@losspay.com" 
                className="text-blue-600 hover:underline text-sm"
              >
                support@losspay.com
              </a>
            </div>

            <div className="bg-white rounded-xl border border-slate-200 p-5 shadow-sm">
              <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center mb-3">
                <Phone className="h-5 w-5 text-green-600" />
              </div>
              <h3 className="font-semibold text-slate-900 mb-1">Call Us</h3>
              <a 
                href="tel:1-800-LOSSPAY" 
                className="text-green-600 hover:underline text-sm"
              >
                1-800-LOSSPAY
              </a>
              <p className="text-xs text-slate-500 mt-1">Mon-Fri, 9am-5pm EST</p>
            </div>

            <div className="bg-amber-50 rounded-xl border border-amber-200 p-5">
              <div className="w-10 h-10 bg-amber-100 rounded-lg flex items-center justify-center mb-3">
                <Car className="h-5 w-5 text-amber-600" />
              </div>
              <h3 className="font-semibold text-slate-900 mb-1">Vehicle Eligibility</h3>
              <p className="text-sm text-slate-600">
                LossPay covers standard passenger vehicles including cars, SUVs, pickups, and vans.
              </p>
            </div>
          </div>

          {/* Contact Form */}
          <div className="md:col-span-2">
            <div className="bg-white rounded-xl border border-slate-200 p-6 sm:p-8 shadow-sm">
              <h2 className="text-xl font-semibold text-slate-900 mb-6" style={{ fontFamily: 'Manrope, sans-serif' }}>
                Send Us a Message
              </h2>
              
              <form onSubmit={handleSubmit} className="space-y-5">
                <div className="grid sm:grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="name" className="text-sm font-medium text-slate-700">
                      Your Name <span className="text-red-500">*</span>
                    </Label>
                    <Input
                      id="name"
                      value={formData.name}
                      onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                      placeholder="John Smith"
                      className="mt-1"
                      required
                    />
                  </div>
                  <div>
                    <Label htmlFor="email" className="text-sm font-medium text-slate-700">
                      Email Address <span className="text-red-500">*</span>
                    </Label>
                    <Input
                      id="email"
                      type="email"
                      value={formData.email}
                      onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                      placeholder="john@example.com"
                      className="mt-1"
                      required
                    />
                  </div>
                </div>

                <div className="grid sm:grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="category" className="text-sm font-medium text-slate-700">
                      Category
                    </Label>
                    <Select
                      value={formData.category}
                      onValueChange={(value) => setFormData({ ...formData, category: value })}
                    >
                      <SelectTrigger className="mt-1">
                        <SelectValue placeholder="Select a category" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="vehicle_eligibility">Vehicle Eligibility</SelectItem>
                        <SelectItem value="quote_question">Quote Question</SelectItem>
                        <SelectItem value="coverage_info">Coverage Information</SelectItem>
                        <SelectItem value="payment">Payment Issue</SelectItem>
                        <SelectItem value="claim">Claims Question</SelectItem>
                        <SelectItem value="other">Other</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label htmlFor="vin" className="text-sm font-medium text-slate-700">
                      VIN (if applicable)
                    </Label>
                    <Input
                      id="vin"
                      value={formData.vin}
                      onChange={(e) => setFormData({ ...formData, vin: e.target.value.toUpperCase() })}
                      placeholder="Enter VIN"
                      className="mt-1 font-mono"
                      maxLength={17}
                    />
                  </div>
                </div>

                <div>
                  <Label htmlFor="subject" className="text-sm font-medium text-slate-700">
                    Subject
                  </Label>
                  <Input
                    id="subject"
                    value={formData.subject}
                    onChange={(e) => setFormData({ ...formData, subject: e.target.value })}
                    placeholder="Brief description of your inquiry"
                    className="mt-1"
                  />
                </div>

                <div>
                  <Label htmlFor="message" className="text-sm font-medium text-slate-700">
                    Message <span className="text-red-500">*</span>
                  </Label>
                  <Textarea
                    id="message"
                    value={formData.message}
                    onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                    placeholder="Please describe how we can help you..."
                    className="mt-1 min-h-[120px]"
                    required
                  />
                </div>

                <Button
                  type="submit"
                  variant="blue"
                  className="w-full sm:w-auto"
                  disabled={submitting}
                >
                  {submitting ? (
                    <>
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                      Sending...
                    </>
                  ) : (
                    <>
                      <Send className="h-4 w-4 mr-2" />
                      Send Message
                    </>
                  )}
                </Button>
              </form>
            </div>
          </div>
        </div>

        {/* FAQ Section */}
        <div className="mt-12">
          <h2 className="text-xl font-semibold text-slate-900 mb-6 text-center" style={{ fontFamily: 'Manrope, sans-serif' }}>
            Frequently Asked Questions
          </h2>
          <div className="grid sm:grid-cols-2 gap-4">
            <div className="bg-white rounded-xl border border-slate-200 p-5">
              <div className="flex gap-3">
                <HelpCircle className="h-5 w-5 text-blue-600 flex-shrink-0 mt-0.5" />
                <div>
                  <h3 className="font-medium text-slate-900 mb-1">What vehicles are eligible?</h3>
                  <p className="text-sm text-slate-600">
                    LossPay covers standard personal-use passenger vehicles including cars, SUVs, pickups, and vans. Trailers, motorcycles, and commercial vehicles are not currently eligible.
                  </p>
                </div>
              </div>
            </div>
            <div className="bg-white rounded-xl border border-slate-200 p-5">
              <div className="flex gap-3">
                <FileQuestion className="h-5 w-5 text-blue-600 flex-shrink-0 mt-0.5" />
                <div>
                  <h3 className="font-medium text-slate-900 mb-1">Where do I find my VIN?</h3>
                  <p className="text-sm text-slate-600">
                    Your VIN can be found on your vehicle registration, insurance card, or on the driver-side dashboard near the windshield.
                  </p>
                </div>
              </div>
            </div>
            <div className="bg-white rounded-xl border border-slate-200 p-5">
              <div className="flex gap-3">
                <HelpCircle className="h-5 w-5 text-blue-600 flex-shrink-0 mt-0.5" />
                <div>
                  <h3 className="font-medium text-slate-900 mb-1">What is Diminished Value Protection?</h3>
                  <p className="text-sm text-slate-600">
                    Diminished Value Protection covers the loss in your vehicle's resale value after an accident, even after repairs are completed.
                  </p>
                </div>
              </div>
            </div>
            <div className="bg-white rounded-xl border border-slate-200 p-5">
              <div className="flex gap-3">
                <FileQuestion className="h-5 w-5 text-blue-600 flex-shrink-0 mt-0.5" />
                <div>
                  <h3 className="font-medium text-slate-900 mb-1">My VIN was rejected. What now?</h3>
                  <p className="text-sm text-slate-600">
                    If your VIN was rejected, please verify it's entered correctly. If you believe your vehicle should qualify, contact us with your VIN and we'll review your case.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Complaint Guidance Section */}
      <div className="max-w-4xl mx-auto px-4 py-8" data-testid="public-complaint-guidance">
        <div className="bg-orange-50 border border-orange-200 rounded-xl p-5">
          <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4">
            <div className="flex-1">
              <h3 className="font-semibold text-orange-900 mb-1">Have a Complaint?</h3>
              <p className="text-sm text-orange-800">
                If you're not satisfied with our service, you have the right to file a complaint with us or your state insurance department.
                Filing a complaint will not affect your policy or pending claims.
              </p>
            </div>
            <div className="flex gap-2">
              <a 
                href="mailto:complaints@losspay.com"
                className="inline-flex items-center px-4 py-2 bg-orange-100 hover:bg-orange-200 text-orange-800 rounded-lg text-sm font-medium transition-colors"
              >
                <Mail className="h-4 w-4 mr-2" />
                complaints@losspay.com
              </a>
            </div>
          </div>
        </div>
      </div>

      {/* Footer */}
      <footer className="bg-slate-900 text-white py-8 mt-12">
        <div className="max-w-4xl mx-auto px-4 text-center">
          <p className="text-slate-400 text-sm">
            © 2026 LossPay Risk Technologies, Inc. All rights reserved.
          </p>
        </div>
      </footer>
    </div>
  );
}
